import React from 'react';
import { Tab, TabGroup, TabList, TabPanel } from '@headlessui/react';
import { FormattedMessage, useIntl } from 'react-intl';

import PageBaseProps from 'types/PageBase';
import { AbstractMedia, MediaType } from 'types/AbstractMedia';

import { UPDATE_FIXED_CACHE_KEY } from 'modules/selfServices/constants';

import { b } from 'utils/i18nUtils';
import { useSelector } from 'hooks';
import { useGetSelfServiceByIdQuery, useUpdateSelfServiceMutation } from 'modules/selfServices/service';

import { isPublicDevice } from 'modules/dealers/selectors';
import { getSelectedSelfServiceId } from 'modules/auth/selectors';
import { isAutoplayEnabled } from 'modules/selfServices/selectors';

import PageFooter from 'components/ui/PageFooter';
import FileList from '../ui/FileList';
import Carousel from '../ui/Carousel';
import Attachments from '../ui/Attachments';

const AppointmentSummary: React.FC<PageBaseProps> = ({
  onNext, onPrev, isPenultimate, shouldDisplayBackButton,
}) => {
  const intl = useIntl();

  const isPublic = useSelector(isPublicDevice);
  const autoplayEnabled = useSelector(isAutoplayEnabled);
  const selfServiceId = useSelector(getSelectedSelfServiceId);

  const { data } = useGetSelfServiceByIdQuery({ id: selfServiceId });
  const [, { isLoading }] = useUpdateSelfServiceMutation({ fixedCacheKey: UPDATE_FIXED_CACHE_KEY });

  const [autoplay, setAutoplay] = React.useState(autoplayEnabled);
  const [isCarouselFullscreen, setCarouselFullscreen] = React.useState(false);

  const { medias, documents } = React.useMemo(
    () => (data.interventionMedias ?? []).reduce((acc, media) => {
      const mediaType = media.type === MediaType.DOCUMENT ? 'documents' : 'medias';
      acc[mediaType].push(media);
      return acc;
    }, { medias: [], documents: [] } as { medias: AbstractMedia[], documents: AbstractMedia[] }),
    [data.interventionMedias],
  );

  const hasDocuments = documents.length > 0;
  const hasMedias = medias.length > 0;

  const hasSelfServiceMedias = data.medias?.length > 0;
  const hasMediasToDisplay = hasMedias || hasDocuments;

  // Appointments medias
  const handleCarouselOpen = React.useCallback(() => setCarouselFullscreen(true), []);
  const handleCarouselClose = React.useCallback(() => setCarouselFullscreen(false), []);

  const handleMediaChange = React.useCallback(() => setAutoplay(false), []);

  return (
    <div className="main-content">
      <main className="content">
        <div className="description text-low text-center">
          <FormattedMessage
            id="page.appointmentSummary.subtitle"
            defaultMessage="We are <b>finished</b> with your vehicle and it is <b>ready to be picked up.</b>"
            values={{ b }}
            tagName="p"
          />
        </div>
        {data.comment && (
          <>
            <div className="w-full h-px bg-border-default" />
            <div className="flex flex-col text-center">
              <FormattedMessage
                id="page.appointmentSummary.dealerMessage.title"
                defaultMessage="A message for you"
                tagName="h2"
              />
              {/* eslint-disable-next-line max-len */}
              <p className="font-medium italic whitespace-pre-wrap before:content-[open-quote] after:content-[close-quote] break-words mt-5">
                {data.comment}
              </p>
            </div>
          </>
        )}

        {hasSelfServiceMedias && (
          <div
            className="flex flex-col rounded-xl overflow-hidden w-full mt-2 kiosk:mt-10"
            data-testid="medias-carousel"
            aria-label={intl.formatMessage({ id: 'accessibility.button.viewMedia', defaultMessage: 'View the media' })}
          >
            <Carousel
              autoplay={autoplay}
              medias={data.medias}
              className="rounded-card"
              onClose={handleCarouselClose}
              fullscreen={isCarouselFullscreen}
              onMediaClick={handleCarouselOpen}
              onMediaChange={handleMediaChange}
            />
          </div>
        )}

        {hasMediasToDisplay && (
          <>
            <div className="w-full h-px bg-border-default my-5" />
            <span className="text-center">
              <FormattedMessage
                id="page.appointmentSummary.mediasAndDocuments.title"
                defaultMessage="Media and documents related to your appointment"
                tagName="h3"
              />
            </span>
            <TabGroup>
              {hasDocuments && hasMedias && (
                <TabList
                  className="flex w-72 xs:w-64 h-12 kiosk:h-24 rounded-full bg-card-bg p-1 mx-auto"
                  data-testid="medias-tab"
                >
                  {hasMedias && (
                    <Tab className="w-full text-center ui-selected:rounded-full ui-selected:bg-white kiosk:text-lg">
                      <FormattedMessage
                        id="page.appointmentSummary.mediasAndDocuments.medias"
                        defaultMessage="Medias"
                      />
                    </Tab>
                  )}
                  {hasDocuments && (
                    <Tab
                      className="w-full text-center ui-selected:rounded-full ui-selected:bg-white kiosk:text-lg"
                      data-testid="documents-tab"
                    >
                      <FormattedMessage
                        id="page.appointmentSummary.mediasAndDocuments.documents"
                        defaultMessage="Documents"
                      />
                    </Tab>
                  )}
                </TabList>
              )}
              {hasMedias && (
                <TabPanel className="w-full m-auto">
                  <FileList
                    medias={medias}
                    autoplay={autoplay}
                    showDownload={!isPublic}
                  />
                </TabPanel>
              )}
              {hasDocuments && (
                <TabPanel className="w-full">
                  <Attachments documents={documents} documentClassName="border-border-low first:border-0 first:pt-0" />
                </TabPanel>
              )}
            </TabGroup>
          </>
        )}
      </main>
      <PageFooter
        onNext={onNext}
        onPrev={onPrev}
        loading={isLoading}
        isPenultimate={isPenultimate}
        shouldDisplayBackButton={shouldDisplayBackButton}
      />
    </div>
  );
};

export default AppointmentSummary;
